import React, { useState, useRef, useEffect } from "react";
import {
  FaMagic,
  FaRedo,
  FaVolumeUp,
  FaPalette,
  FaCheckCircle
} from "react-icons/fa";

export default function TalkingSketchbookGame() {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#FF4500");
  const [brushSize] = useState(12);
  const [detectedItems, setDetectedItems] = useState([]);
  const [aiSpeech, setAiSpeech] = useState(
    "Hi there! Draw a bright sun or a happy cloud on the canvas!"
  );
  const [isGeneratingStory, setIsGeneratingStory] = useState(false);
  const [generatedStory, setGeneratedStory] = useState(null);

  // Setup Canvas Context
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = canvas.offsetWidth || 600;
      canvas.height = canvas.offsetHeight || 350;
      const ctx = canvas.getContext("2d");
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      // Fill background white
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, []);

  // Handle Drawing Inputs (Mouse + Touch support)
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = selectedColor;
    ctx.lineWidth = brushSize;
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();

    simulateVisionDetection();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setDetectedItems([]);
    setGeneratedStory(null);
    speakAi("Canvas cleared! What magical thing shall we draw next?");
  };

  // Mock Vision AI Logic
  const simulateVisionDetection = () => {
    if (selectedColor === "#FFD700" && !detectedItems.includes("Golden Sun")) {
      setDetectedItems((prev) => [...prev, "Golden Sun"]);
      speakAi("Ooh! I see a bright golden circle! Is that a warm sun?");
    } else if (
      selectedColor === "#1E90FF" &&
      !detectedItems.includes("Fluffy Cloud")
    ) {
      setDetectedItems((prev) => [...prev, "Fluffy Cloud"]);
      speakAi("I see beautiful blue shapes! Looks like fluffy clouds!");
    } else if (
      selectedColor === "#32CD32" &&
      !detectedItems.includes("Green Tree")
    ) {
      setDetectedItems((prev) => [...prev, "Green Tree"]);
      speakAi("Yay, green! Are we making a magical forest?");
    }
  };

  const speakAi = (text) => {
    setAiSpeech(text);
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.pitch = 1.2;
      utterance.rate = 0.9;
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleGenerateStory = () => {
    if (detectedItems.length === 0) {
      speakAi("Draw something colorful on the canvas first!");
      return;
    }

    setIsGeneratingStory(true);
    speakAi("Thinking of a magical story about your drawing...");

    setTimeout(() => {
      const storyText = `Once upon a time, high in the sky, there was a ${detectedItems.join(
        " and a "
      )}. Every morning, they danced together in the warm breeze, bringing big smiles to all the little animals in the forest!`;
      setGeneratedStory(storyText);
      setIsGeneratingStory(false);
      speakAi(storyText);
    }, 2000);
  };

  const colors = [
    { name: "Sun Yellow", hex: "#FFD700" },
    { name: "Sky Blue", hex: "#1E90FF" },
    { name: "Grass Green", hex: "#32CD32" },
    { name: "Red", hex: "#FF4500" },
    { name: "Purple", hex: "#9370DB" }
  ];

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconCircle}>
              <FaMagic size={22} color="#f59e0b" />
            </div>
            <div>
              <h1 style={styles.title}>The Talking Sketchbook</h1>
              <p style={styles.subtitle}>
                AI-Powered Nursery Drawing & Story Time
              </p>
            </div>
          </div>

          <button onClick={clearCanvas} style={styles.resetBtn}>
            <FaRedo size={14} /> Reset
          </button>
        </div>

        {/* Speech Banner */}
        <div style={styles.speechBanner}>
          <FaVolumeUp size={20} color="#d97706" style={{ flexShrink: 0 }} />
          <p style={styles.speechText}>"{aiSpeech}"</p>
        </div>

        {/* Canvas Area */}
        <div style={styles.canvasContainer}>
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            style={styles.canvas}
          />

          {/* AI Tags */}
          <div style={styles.tagOverlay}>
            {detectedItems.map((item, idx) => (
              <span key={idx} style={styles.tag}>
                <FaCheckCircle size={12} /> AI Saw: {item}
              </span>
            ))}
          </div>
        </div>

        {/* Palette Controls */}
        <div style={styles.toolbar}>
          <div style={styles.colorPalette}>
            <FaPalette size={18} color="#64748b" style={{ marginRight: 6 }} />
            {colors.map((color) => (
              <button
                key={color.hex}
                onClick={() => setSelectedColor(color.hex)}
                style={{
                  ...styles.colorBtn,
                  backgroundColor: color.hex,
                  transform:
                    selectedColor === color.hex ? "scale(1.15)" : "scale(1)",
                  border:
                    selectedColor === color.hex
                      ? "3px solid #1e293b"
                      : "3px solid #ffffff"
                }}
                title={color.name}
              />
            ))}
          </div>

          <button
            onClick={handleGenerateStory}
            disabled={isGeneratingStory || detectedItems.length === 0}
            style={{
              ...styles.storyBtn,
              opacity: detectedItems.length > 0 ? 1 : 0.5,
              cursor: detectedItems.length > 0 ? "pointer" : "not-allowed"
            }}
          >
            <FaMagic size={16} />
            <span>
              {isGeneratingStory ? "Creating Story..." : "Tell Me A Story!"}
            </span>
          </button>
        </div>

        {/* Story Output */}
        {generatedStory && (
          <div style={styles.storySection}>
            <h3 style={styles.storyHeader}>
              <FaMagic color="#7c3aed" /> Your AI Drawing Story
            </h3>
            <p style={styles.storyBox}>{generatedStory}</p>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#fffbeb",
    padding: "20px",
    boxSizing: "border-box",
    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif"
  },
  card: {
    width: "100%",
    maxWidth: "800px",
    backgroundColor: "#ffffff",
    borderRadius: "24px",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
    border: "4px solid #fcd34d",
    overflow: "hidden"
  },
  header: {
    background: "linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)",
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#ffffff"
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  iconCircle: {
    backgroundColor: "#ffffff",
    borderRadius: "50%",
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  title: {
    margin: 0,
    fontSize: "1.4rem",
    fontWeight: 800
  },
  subtitle: {
    margin: 0,
    fontSize: "0.85rem",
    opacity: 0.95
  },
  resetBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#ef4444",
    color: "#ffffff",
    border: "none",
    padding: "8px 16px",
    borderRadius: "12px",
    fontWeight: "bold",
    cursor: "pointer"
  },
  speechBanner: {
    backgroundColor: "#fef3c7",
    padding: "12px 20px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    borderBottom: "2px solid #fde68a"
  },
  speechText: {
    margin: 0,
    color: "#78350f",
    fontWeight: "600",
    fontStyle: "italic",
    fontSize: "0.95rem"
  },
  canvasContainer: {
    position: "relative",
    width: "100%",
    height: "360px",
    backgroundColor: "#ffffff",
    touchAction: "none"
  },
  canvas: {
    width: "100%",
    height: "100%",
    cursor: "crosshair"
  },
  tagOverlay: {
    position: "absolute",
    top: "12px",
    right: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  tag: {
    backgroundColor: "#22c55e",
    color: "#ffffff",
    fontSize: "0.75rem",
    fontWeight: "bold",
    padding: "4px 10px",
    borderRadius: "20px",
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  toolbar: {
    backgroundColor: "#f1f5f9",
    padding: "16px 20px",
    borderTop: "2px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px"
  },
  colorPalette: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  colorBtn: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    cursor: "pointer",
    transition: "transform 0.15s ease",
    boxShadow: "0 2px 5px rgba(0,0,0,0.15)"
  },
  storyBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
    color: "#ffffff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "14px",
    fontWeight: "800",
    fontSize: "0.95rem"
  },
  storySection: {
    padding: "20px",
    backgroundColor: "#f5f3ff",
    borderTop: "2px solid #ddd6fe"
  },
  storyHeader: {
    margin: "0 0 10px 0",
    color: "#5b21b6",
    fontSize: "1.1rem",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  storyBox: {
    margin: 0,
    backgroundColor: "#ffffff",
    padding: "16px",
    borderRadius: "12px",
    color: "#4c1d95",
    lineHeight: "1.5",
    fontSize: "0.98rem",
    border: "1px solid #e9d5ff"
  }
};